/**
 * 操作日志工具 - 前端操作日志记录
 *
 * 功能：
 * 1. 自动记录敏感操作（删除、修改、导出等）
 * 2. 调用后端日志接口持久化存储
 * 3. 本地缓存失败的日志，网络恢复后重试
 */

import request from './request'
import { getUserInfo } from './auth'

export interface OperationLogData {
  module: string
  action: string
  url: string
  method: string
  duration: number
  status: number
  targetName: string
  detail: string
}

// 本地缓存的待发送日志（网络异常时使用）
const PENDING_LOGS_KEY = 'admin_pending_logs'

/**
 * 记录操作日志（调用后端接口）
 */
export const logOperation = async (log: OperationLogData): Promise<void> => {
  try {
    const userInfo = getUserInfo()

    // 调用后端日志接口
    await request.post('/settings/logs', {
      ...log,
      operatorId: userInfo?.id,
      operatorName: userInfo?.username || userInfo?.realName,
      timestamp: new Date().toISOString(),
    })
  } catch (error) {
    // 记录失败时保存到本地队列，稍后重试
    savePendingLog(log)
  }
}

/**
 * 手动记录操作日志（用于需要自定义内容的场景）
 * @param module 模块名
 * @param action 操作名
 * @param targetName 操作对象名
 * @param detail 详细描述
 */
export const recordOperation = (
  module: string,
  action: string,
  targetName: string = '',
  detail: string = ''
): void => {
  const userInfo = getUserInfo()

  // 异步发送，不阻塞主流程
  request
    .post('/settings/logs', {
      module,
      action,
      targetName,
      detail,
      operatorId: userInfo?.id,
      operatorName: userInfo?.username || userInfo?.realName,
      url: window.location.pathname,
      method: 'MANUAL',
      duration: 0,
      status: 200,
      timestamp: new Date().toISOString(),
    })
    .catch(() => {
      // 失败也保存到本地
      savePendingLog({
        module,
        action,
        url: window.location.pathname,
        method: 'MANUAL',
        duration: 0,
        status: 200,
        targetName,
        detail,
      })
    })
}

/**
 * 保存待发送日志到本地
 */
const savePendingLog = (log: OperationLogData): void => {
  try {
    const logs = getPendingLogs()
    logs.push({ ...log, _savedAt: Date.now() })
    // 最多保留 100 条
    if (logs.length > 100) {
      logs.splice(0, logs.length - 100)
    }
    localStorage.setItem(PENDING_LOGS_KEY, JSON.stringify(logs))
  } catch (e) {
    // 存储失败忽略
  }
}

/**
 * 获取待发送日志
 */
const getPendingLogs = (): any[] => {
  try {
    const str = localStorage.getItem(PENDING_LOGS_KEY)
    return str ? JSON.parse(str) : []
  } catch (e) {
    return []
  }
}

/**
 * 重发所有待发送日志
 */
export const flushPendingLogs = async (): Promise<void> => {
  const logs = getPendingLogs()
  if (logs.length === 0) return

  const userInfo = getUserInfo()
  let successCount = 0

  for (const log of logs) {
    try {
      await request.post('/settings/logs', {
        ...log,
        operatorId: userInfo?.id,
        operatorName: userInfo?.username || userInfo?.realName,
      })
      successCount++
    } catch (e) {
      // 继续发送下一条
    }
  }

  // 全部成功则清空
  if (successCount === logs.length) {
    localStorage.removeItem(PENDING_LOGS_KEY)
  } else {
    // 只保留失败的（这里简单处理，全部保留）
    // 实际项目中应该逐条标记失败状态
  }
}
